import React from 'react';
import { useQuerySet } from '~/contexts/QuerysetContext';
import { ResultFullText } from '~/core/results/types/results.type';
import { GoToResultHandler } from '~/hooks/useGoToResult';
import { useShownAttributes } from '~/hooks/useShownAttributes';
import { DynamicComponents } from '~/plugins/dynamicComponents/dynamicComponents';
import SanitizedHTML from '~/utils/components/SanitizedHTML';
import { getResultContent } from '~/utils/getResultContent';
import getResultTitle from '~/utils/getResultTitle';
import styles from './fulltextResult.module.scss';

export interface FullTextSingleResultProps {
    result: ResultFullText;
    hasIcons: boolean;
    handleGoToResult: GoToResultHandler;
    order: number;
    renderProps: {
        FulltextResultHeading: DynamicComponents['FulltextResultHeading'];
        FulltextResultWrapper: DynamicComponents['FulltextResultWrapper'];
    };
}

const FulltextSingleResult: React.FC<FullTextSingleResultProps> = ({
    result,
    hasIcons,
    handleGoToResult,
    order,
    renderProps: { FulltextResultHeading, FulltextResultWrapper },
}) => {
    const { state: querySetState } = useQuerySet();
    const { shouldRenderKey } = useShownAttributes({ querySetState });

    return (
        <FulltextResultWrapper hasIcons={hasIcons}>
            <FulltextResultHeading result={result} hasIcons={hasIcons}>
                <div
                    role="link"
                    tabIndex={0}
                    className={styles.serpResultLink}
                    onMouseDown={(e) =>
                        handleGoToResult({
                            url: result.url,
                            order,
                            resultType: 'fulltext',
                            openInNewTab: e.button === 1 || (e.button === 0 && e.ctrlKey),
                            button: e.button,
                            key: e.ctrlKey ? 'ctrl' : undefined,
                        })
                    }
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                            e.preventDefault();
                            e.stopPropagation();
                            handleGoToResult({ url: result.url, order, resultType: 'category', key: 'Enter' });
                        }
                    }}
                >
                    <SanitizedHTML html={getResultTitle(result)} />
                </div>
            </FulltextResultHeading>
            {shouldRenderKey('rawUrl') ? (
                <div role="paragraph" className={styles.serpResultUrl} data-testid="fulltext-resulturl">
                    <small>{result.url}</small>
                </div>
            ) : null}
            {shouldRenderKey('content') ? (
                <div role="paragraph" className={styles.serpResultContent} data-testid="fulltext-resultcontent">
                    <SanitizedHTML html={getResultContent(result)} />
                </div>
            ) : null}
        </FulltextResultWrapper>
    );
};

export default FulltextSingleResult;
